/** Task-debt (overdue) pipeline math — pure, unit-tested.
 *  Philosophy: overdue work is DEBT — oldest first, each item gets a clear
 *  verdict (done today / moved to another day / deleted), never silent rot. */

export interface OverdueLike {
  id: string;
  title: string;
  dueDate: string;
  status: string;
  courseTitle?: string;
  kind?: string;
}

export function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Whole days a task is past due (≥1 for anything in the overdue list). */
export function daysLate(dueDate: string, today: string): number {
  const [y1, m1, d1] = dueDate.split("-").map(Number);
  const [y2, m2, d2] = today.split("-").map(Number);
  const diff = Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1);
  return Math.max(1, Math.round(diff / 86_400_000));
}

/** The day after a YYYY-MM-DD key — the "+1 day" move target. */
export function nextDayKey(dayKey: string): string {
  const [y, m, d] = dayKey.split("-").map(Number);
  return dateKey(new Date(y, m - 1, d + 1));
}

/**
 * The overdue verdict list: OPEN work only, oldest debt first, newest-date
 * client data already filtered server-side — this is a defensive re-check.
 */
export function buildOverdueList(tasks: OverdueLike[], today: string): OverdueLike[] {
  return tasks
    .filter((task) => task.status === "open" || task.status === "in_progress")
    .filter((task) => task.dueDate < today)
    .sort((a, b) => (a.dueDate === b.dueDate ? a.title.localeCompare(b.title) : a.dueDate < b.dueDate ? -1 : 1));
}
